import type { IObservableArray } from 'mobx';
import { useMemo } from 'react';

import type { Campaign, DisplayCreative, TChannel } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';
import useMetaIntegrationStatus from '@feathr/extender/hooks/useMetaIntegrationStatus';
import { useAccount } from '@feathr/extender/state';

import { useMetaIntegration } from '../../Settings/Account/MetaIntegrationPage/MetaIntegrationPage.hooks';

function integrationToChannel(integration: EIntegrationTypes): TChannel | undefined {
  switch (integration) {
    case EIntegrationTypes.Tradedesk:
      return 'display';

    case EIntegrationTypes.Meta:
      return 'meta';

    default:
      return undefined;
  }
}

function integrationsToChannels(integrations: EIntegrationTypes[] | undefined): TChannel[] {
  if (!integrations) {
    return [];
  }
  return integrations
    .map(integrationToChannel)
    .filter((channel): channel is TChannel => channel !== undefined);
}

export interface IUseChannelAvailabilityParams {
  campaign: Campaign;
  creatives?: IObservableArray<DisplayCreative>;
}

export interface IUseChannelAvailabilityResult {
  /** Channels selected on the campaign (from integrations for Omni, `['display']` otherwise) */
  selectedChannels: TChannel[];
  /** Channels available for adding creatives (filters out meta for monetization, Lite tier, or no integration) */
  availableChannels: TChannel[];
  /** Whether Display channel is selected */
  displaySelected: boolean;
  /** Whether Meta channel is selected */
  metaSelected: boolean;
  /** Whether any Display creatives exist */
  hasDisplayCreatives: boolean;
  /** Whether any Meta creatives exist */
  hasMetaCreatives: boolean;
  /** Whether the account is on Platform Lite tier */
  isPlatformLite: boolean;
  /** Whether the account has an existing Meta integration */
  hasExistingIntegration: boolean;
}

/**
 * Hook that centralizes all channel-related logic for a campaign.
 *
 * @param params - Campaign and optional creatives array
 * @returns Channel selection state and availability
 *
 * @example
 * const { availableChannels, hasDisplayCreatives } = useChannelAvailability({
 *   campaign,
 *   creatives: creatives.models,
 * });
 */
export function useChannelAvailability({
  campaign,
  creatives,
}: IUseChannelAvailabilityParams): IUseChannelAvailabilityResult {
  const account = useAccount();

  // `connected`, `configuring`, and `no_active_pages` all count as "has
  // existing integration": the integration itself is set up; lacking pages
  // is a transient condition surfaced on the Meta integration page, not a
  // reason to hide the channel from the campaign editor. `error` and
  // `not_connected` exclude the channel.
  const { isLoading: isLoadingMetaIntegration, status: metaStatus } = useMetaIntegration();
  const hasNewIntegration =
    metaStatus === 'connected' || metaStatus === 'configuring' || metaStatus === 'no_active_pages';

  // Fall back to the legacy account-based check while the new endpoint loads,
  // to avoid the Meta channel briefly appearing unavailable on first paint.
  const { hasExistingIntegration: legacyHasExistingIntegration } = useMetaIntegrationStatus();
  const hasExistingIntegration = isLoadingMetaIntegration
    ? legacyHasExistingIntegration
    : hasNewIntegration;

  const { isOmniCampaign, isMonetization } = campaign;
  const integrations = campaign.get('integrations');
  const isPlatformLite = account.isPlatformLite;

  // Meta is unavailable if: monetization campaign, Lite tier, or no Meta integration set up
  const isMetaUnavailable = isMonetization || isPlatformLite || !hasExistingIntegration;

  // Non-Omni campaigns are display-only; Omni campaigns derive channels from selected integrations.
  const selectedChannels = useMemo<TChannel[]>(
    () => (isOmniCampaign ? integrationsToChannels(integrations) : ['display']),
    [isOmniCampaign, integrations],
  );

  const availableChannels = useMemo<TChannel[]>(
    () => selectedChannels.filter((ch) => !(ch === 'meta' && isMetaUnavailable)),
    [selectedChannels, isMetaUnavailable],
  );

  const displaySelected = selectedChannels.includes('display');
  const metaSelected = selectedChannels.includes('meta');

  // Include creatives?.length in dependencies to re-compute when creatives are loaded/changed
  const hasDisplayCreatives = useMemo(
    () => creatives?.some((c) => c.channel === 'display') ?? false,
    [creatives, creatives?.length],
  );

  const hasMetaCreatives = useMemo(
    () => creatives?.some((c) => c.channel === 'meta') ?? false,
    [creatives, creatives?.length],
  );

  return {
    selectedChannels,
    availableChannels,
    displaySelected,
    metaSelected,
    hasDisplayCreatives,
    hasMetaCreatives,
    isPlatformLite,
    hasExistingIntegration,
  };
}
